import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const DEFAULT_PAGE_OPTIONS = [7, 10, 20, 50];

function getPageNumbers(currentPage, totalPages) {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  if (currentPage <= 3) {
    return [1, 2, 3, 4, "...", totalPages];
  }
  if (currentPage >= totalPages - 2) {
    return [1, "...", totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }
  return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
}

/**
 * Reusable pagination bar.
 * Props:
 *   total          — total number of items
 *   perPage        — items per page (controlled)
 *   currentPage    — current page (controlled, 1-based)
 *   onPageChange   — (page: number) => void
 *   onPerPageChange — (perPage: number) => void  (also resets to page 1)
 *   pageOptions    — array of numbers, default [7, 10, 20, 50]
 *
 * Returns null if total <= smallest pageOption (no pagination needed).
 */
export function PaginationControls({
  total,
  perPage,
  currentPage,
  onPageChange,
  onPerPageChange,
  pageOptions = DEFAULT_PAGE_OPTIONS,
}) {
  const totalPages = Math.ceil(total / perPage);
  const startItem = total === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const endItem = Math.min(currentPage * perPage, total);

  if (total <= pageOptions[0]) return null;

  const pageNumbers = getPageNumbers(currentPage, totalPages);

  const btnStyle = (active, disabled) => ({
    border: "none",
    cursor: disabled ? "default" : "pointer",
    fontFamily: "IBM Plex Sans, sans-serif",
    fontSize: "0.875rem",
    borderRadius: "6px",
    padding: "5px 9px",
    minWidth: "32px",
    height: "32px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background 0.15s",
    background: active ? "var(--rv-teal)" : disabled ? "transparent" : "var(--rv-paper)",
    color: active ? "white" : disabled ? "var(--rv-text-muted)" : "var(--rv-text-primary)",
    fontWeight: active ? 600 : 400,
    opacity: disabled ? 0.4 : 1,
  });

  return (
    <div
      className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 mt-2 border-t"
      style={{ borderColor: "var(--rv-border)" }}
    >
      {/* Left: showing info */}
      <span
        className="text-sm order-2 sm:order-1"
        style={{ color: "var(--rv-text-muted)", fontFamily: "IBM Plex Sans, sans-serif" }}
      >
        Показано {startItem}–{endItem} из {total}
      </span>

      {/* Center: page buttons */}
      <div className="flex items-center gap-1 order-1 sm:order-2">
        <button
          data-testid="pagination-prev"
          onClick={() => currentPage > 1 && onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          style={btnStyle(false, currentPage === 1)}
        >
          <ChevronLeft size={15} />
        </button>

        {pageNumbers.map((p, i) =>
          p === "..." ? (
            <span
              key={`ellipsis-${i}`}
              className="px-1 text-sm select-none"
              style={{ color: "var(--rv-text-muted)" }}
            >
              …
            </span>
          ) : (
            <button
              key={p}
              data-testid={`pagination-page-${p}`}
              onClick={() => onPageChange(p)}
              style={btnStyle(currentPage === p, false)}
            >
              {p}
            </button>
          )
        )}

        <button
          data-testid="pagination-next"
          onClick={() => currentPage < totalPages && onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          style={btnStyle(false, currentPage === totalPages)}
        >
          <ChevronRight size={15} />
        </button>
      </div>

      {/* Right: per-page selector */}
      <div className="flex items-center gap-2 order-3">
        <span
          className="text-sm"
          style={{ color: "var(--rv-text-muted)", fontFamily: "IBM Plex Sans, sans-serif" }}
        >
          На странице:
        </span>
        <select
          data-testid="pagination-per-page"
          value={perPage}
          onChange={(e) => {
            onPerPageChange(Number(e.target.value));
            onPageChange(1);
          }}
          style={{
            border: "1px solid var(--rv-border)",
            color: "var(--rv-text-primary)",
            fontFamily: "IBM Plex Sans, sans-serif",
            background: "white",
            borderRadius: "6px",
            fontSize: "0.875rem",
            padding: "4px 8px",
            height: "32px",
            cursor: "pointer",
          }}
        >
          {pageOptions.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
